import React, { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ManagerDataService from "../services/manager.service";
import AdminDataService from "../services/admin.service";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ProfileModal from "./profile-modal.component";
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import axios from 'axios';
import logger from '../utils/logger';
import { setAuthToken } from "../http-common";

const ProfilButton = () => {
  const { 
    user, 
    logout, 
    isAuthenticated, 
    getAccessTokenSilently,
    loginWithRedirect
  } = useAuth0();
  
  const emailu = user?.email;
  // Initialisation correcte de currentHab avec des valeurs par défaut
  const [currentHab, setCurrentHab] = useState({
    admin: 0,
    mng: 0,
    sec: 0,
    med: 0
  });
  const [recetteCounts, setRecetteCounts] = useState({
    admin: 0,
    manager: 0
  });
  const [nomMissing, setNomMissing] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  const getHabilitation = useCallback(async (email) => {
    if (!email) return;
    
    try {
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: "openid profile email"
      });

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await ManagerDataService.habilitation(email);
      
      // Vérification et initialisation sécurisée de currentHab
      if (response?.data?.[0]) {
        setCurrentHab({
          admin: response.data[0].admin || 0,
          mng: response.data[0].mng || 0,
          sec: response.data[0].sec || 0,
          med: response.data[0].med || 0
        });
      }
    } catch (e) {
      logger.error('Erreur lors de la récupération des habilitations:', e);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const checkProfile = async () => {
      if (emailu && isAuthenticated) {
        try {
          const res = await AdminDataService.getUserNameByEmail(emailu);
          const p = res?.data || null;
          const missing = !p || !p.nom || String(p.nom).trim() === "";
          setNomMissing(missing);
          if (missing) {
            setShowProfileModal(true);
          }
        } catch (e) {
          setNomMissing(true);
          setShowProfileModal(true);
        }
      }
    };

    const initialize = async () => {
      if (emailu && isAuthenticated) {
        try {
          const token = await getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "openid profile email"
          });
          if (token) {
            setAuthToken(token);
          }
          await getHabilitation(emailu);
          try {
            const [adminCountRes, mngCountRes] = await Promise.all([
              AdminDataService.getRecetteAdminCount(emailu),
              ManagerDataService.getRecetteMngCount(emailu)
            ]);
            const adminCount = Number(adminCountRes?.data?.[0]?.recette_admin || adminCountRes?.data?.recette_admin || adminCountRes?.data?.count || adminCountRes?.data?.[0]?.count || 0);
            const managerCount = Number(mngCountRes?.data?.[0]?.recette_mng || mngCountRes?.data?.recette_mng || mngCountRes?.data?.count || mngCountRes?.data?.[0]?.count || 0);
            setRecetteCounts({
              admin: Number.isFinite(adminCount) ? adminCount : 0,
              manager: Number.isFinite(managerCount) ? managerCount : 0
            });
          } catch (e) {
            logger.error('Erreur lors de la récupération des compteurs recettes:', e);
            setRecetteCounts({ admin: 0, manager: 0 });
          }
          await checkProfile();
        } catch (e) {
          logger.error('Erreur initialisation:', e);
        }
      }
    };
    
    initialize();
  }, [emailu, isAuthenticated, getAccessTokenSilently, getHabilitation]);

  if (!isAuthenticated) {
    return (
      <li className="nav-item">
        <button 
          className="btn btn-outline-primary" 
          onClick={() => loginWithRedirect()}
        >
          <FontAwesomeIcon icon={solid('user')}/> Connexion
        </button>
      </li>
    );
  }

  return (
    <li className="nav-item dropdown">
      <a className="nav-link dropdown-toggle" href="/" role="button" data-bs-toggle="dropdown" aria-expanded="false">
        {nomMissing && <span className="text-danger me-1"><FontAwesomeIcon icon={solid('circle-exclamation')} /></span>}
        <u>{user?.email}</u>
      </a>
      <ul className="dropdown-menu dropdown-menu-end">
        <li><a className="dropdown-item" href="/mon-profil"><FontAwesomeIcon icon={solid('user')} className="text-secondary"/> Profil</a></li>
        <li><hr className="dropdown-divider"/></li>
        {currentHab?.admin === 1 && <li><a className="dropdown-item" href="/homeAdmin"><FontAwesomeIcon icon={solid('screwdriver-wrench')} className="text-secondary"/> Administration</a><hr className="dropdown-divider"/></li>}
        {currentHab?.mng === 1 && <li><a className="dropdown-item" href="/homeMng"><FontAwesomeIcon icon={solid('calendar-alt')} className="text-secondary"/> Gestion des plannings</a><hr className="dropdown-divider"/></li>}
        {currentHab?.sec === 1 && <li><a className="dropdown-item" href="/homeSec"><FontAwesomeIcon icon={solid('person-walking-arrow-right')} className="text-secondary"/> Admissions</a><hr className="dropdown-divider"/></li>}
        {currentHab?.med === 1 && <li><a className="dropdown-item" href="/homeMed"><FontAwesomeIcon icon={solid('stethoscope')} className="text-secondary"/> Consultations</a><hr className="dropdown-divider"/></li>}
        {recetteCounts?.admin > 0 && <li><a className="dropdown-item" href="/recette-globale"><FontAwesomeIcon icon={solid('sack-dollar')} className="text-secondary"/> Recettes Globales</a><hr className="dropdown-divider"/></li>}
        {recetteCounts?.manager > 0 && <li><a className="dropdown-item" href="/recette-structure"><FontAwesomeIcon icon={solid('hand-holding-dollar')} className="text-secondary"/> Recettes</a><hr className="dropdown-divider"/></li>}
        <li><a className="dropdown-item" href="/homePat"><FontAwesomeIcon icon={solid('calendar-check')} className="text-secondary"/> Rendez-vous</a><hr className="dropdown-divider"/></li>
        <li>
          <a 
            className="dropdown-item" 
            href="/" 
            onClick={(e) => { 
              e.preventDefault(); 
              logout({ 
                logoutParams: { 
                  returnTo: window.location.origin 
                } 
              });
            }}
          >
            <FontAwesomeIcon icon={solid('power-off')} className="text-secondary"/> Déconnexion
          </a>
        </li>
      </ul>
      
      {showProfileModal && (
        <ProfileModal
          isOpen={showProfileModal}
          email={(user?.email || '').trim()}
          onSaved={async () => {
            setShowProfileModal(false);
            try {
              const res = await AdminDataService.getUserNameByEmail(user?.email);
              const p = res?.data || null;
              const missing = !p || !p.nom || String(p.nom).trim() === "";
              setNomMissing(missing);
            } catch (_) {
              setNomMissing(true);
            }
          }}
        />
      )}
    </li>
  );
};

export default ProfilButton;